import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRedoAlt } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@stormwater-software/lib-ui';
import Loading from '../../../Shared/ResourceIndex/Loading';
import SearchInput from '../../../Shared/Search/SearchInput';
import MyProjectsMobileCard, { CardSelection } from './MyProjectsMobileCard';
import MyProjectsMobileWeek from './MyProjectsMobileWeek';
import {
  DueRow,
  FILTER_OPTIONS,
  FilterKey,
  ParseDueDate,
  SORT_OPTIONS,
  CALENDAR_SORT_OPTIONS,
  PROJECT_SORT_OPTIONS,
  SortKey,
  countByFilter,
  filterRows,
  sortRows,
} from './myProjectsMobile.utils';

/** The values stored in localStorage `myProjectsWidgetPreference` (see InspectionsDue.jsx). */
export type MyProjectsView = 'InspectionsDue' | 'Insp. Calendar' | 'ProjectList';

const VIEW_OPTIONS: { key: MyProjectsView; label: string }[] = [
  { key: 'InspectionsDue', label: 'Inspections' },
  { key: 'Insp. Calendar', label: 'Calendar' },
  { key: 'ProjectList', label: 'Projects' },
];

const PAGE_SIZE = 5;

// Mirrors the desktop route planner's cap (InspectionsDue.jsx, the list-view checkbox); display only.
const MAX_STOPS = 10;

type MyProjectsMobileProps = {
  /** Undefined after a failed fetch (the dashboard hook stores `undefined`), not just empty. */
  rows: DueRow[] | undefined;
  isLoading: boolean;
  view: MyProjectsView;
  onViewChange: (view: MyProjectsView) => void;
  /** Refetch the current view's rows (Open Findings' refresh). */
  onRefresh: () => void;
  search: string;
  onSearchChange: (search: string) => void;
  parseDueDate: ParseDueDate;
  /** Tenant has "Enable Route Planner". */
  canSelect: boolean;
  selectedCount: number;
  getSelection: (row: DueRow) => CardSelection;
  onClearSelection: () => void;
  onGetDirections: () => void;
  renderAction: (row: DueRow) => React.ReactNode;
  renderOfflineActions: (row: DueRow) => React.ReactNode;
  renderProjectList: (selectMode: boolean, sort: SortKey) => React.ReactNode;
  /** The attributes that stop the dashboard's drag handle starting a drag from the widget body. */
  dragGuardProps: Record<string, unknown>;
};

/**
 * SW2-2467. The My Projects widget below 768px. Owns only view-local UI state (filter, sort, how many
 * cards show, select mode); rows, selection and every action come from InspectionsDue unchanged.
 */
const MyProjectsMobile: React.FC<MyProjectsMobileProps> = ({
  rows,
  isLoading,
  view,
  onViewChange,
  onRefresh,
  search,
  onSearchChange,
  parseDueDate,
  canSelect,
  selectedCount,
  getSelection,
  onClearSelection,
  onGetDirections,
  renderAction,
  renderOfflineActions,
  renderProjectList,
  dragGuardProps,
}) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  // One order per view: each view has its own options (the calendar's agenda is one day; projects
  // have no due date).
  const [sorts, setSorts] = useState<Record<MyProjectsView, SortKey>>({
    InspectionsDue: 'dueAsc',
    'Insp. Calendar': 'site',
    ProjectList: 'site',
  });
  const sort = sorts[view];
  const setSort = (key: SortKey) => setSorts((current) => ({ ...current, [view]: key }));
  const sortOptions =
    view === 'InspectionsDue'
      ? SORT_OPTIONS
      : view === 'ProjectList'
      ? PROJECT_SORT_OPTIONS
      : CALENDAR_SORT_OPTIONS;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectMode, setSelectMode] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const sortButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter, sort, search, view]);

  const now = new Date();
  const allRows = rows || [];
  // A failed fetch leaves rows undefined; say so rather than "No inspections due".
  const loadFailed = !isLoading && !Array.isArray(rows);
  const loadFailedMessage = (
    <p className='my-projects-mobile__empty' role='alert'>
      Couldn&apos;t load your inspections. Refresh the page to try again.
    </p>
  );
  const counts = countByFilter(allRows, parseDueDate, now);
  const shown = sortRows(filterRows(allRows, filter, parseDueDate, now), sort, parseDueDate);
  const sortLabel = sortOptions.find((option) => option.key === sort)?.label;

  const canSelectHere = canSelect && view !== 'Insp. Calendar';
  const inSelectMode = selectMode && canSelectHere;

  const exitSelectMode = () => {
    setSelectMode(false);
    onClearSelection();
  };

  const changeView = (next: MyProjectsView) => {
    setSelectMode(false);
    onViewChange(next);
  };

  const closeSort = () => {
    setSortOpen(false);
    sortButtonRef.current?.focus();
  };

  const renderCard = (row: DueRow, key: string, selectable: boolean) => (
    <li key={key}>
      <MyProjectsMobileCard
        row={row}
        parseDueDate={parseDueDate}
        now={now}
        action={renderAction(row)}
        offlineActions={renderOfflineActions(row)}
        selection={selectable ? getSelection(row) : undefined}
      />
    </li>
  );

  const sortButton = (
    <button
      ref={sortButtonRef}
      type='button'
      className='my-projects-mobile__text-btn my-projects-mobile__sort'
      aria-haspopup='dialog'
      onClick={() => setSortOpen(true)}
    >
      Sort: {sortLabel}
    </button>
  );

  const renderInspections = () => (
    <>
      <div className='my-projects-mobile__tiles' role='group' aria-label='Filter by due date'>
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.key}
            type='button'
            className={classNames(
              'my-projects-mobile__tile',
              `my-projects-mobile__tile--${option.key}`,
            )}
            aria-pressed={filter === option.key}
            onClick={() => setFilter(option.key)}
          >
            <span className='my-projects-mobile__tile-count'>{counts[option.key]}</span>
            <span className='my-projects-mobile__tile-label'>{option.label}</span>
          </button>
        ))}
      </div>

      {sortButton}

      {isLoading ? (
        <Loading what='data' showLoadingText={false} />
      ) : loadFailed ? (
        loadFailedMessage
      ) : shown.length ? (
        <ul className='my-projects-mobile__list'>
          {shown
            .slice(0, visibleCount)
            .map((row, index) =>
              renderCard(row, `${row.projectId}_${row.inspectionId}_${index}`, inSelectMode),
            )}
        </ul>
      ) : (
        <p className='my-projects-mobile__empty'>
          {filter === 'all' && !search ? 'No inspections due.' : 'No inspections match.'}
        </p>
      )}

      {!isLoading && shown.length > visibleCount && (
        <button
          type='button'
          className='my-projects-mobile__show-more'
          onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
        >
          Show {Math.min(PAGE_SIZE, shown.length - visibleCount)} more
        </button>
      )}
      <p className='my-projects-mobile__sr-only' aria-live='polite'>
        Showing {Math.min(visibleCount, shown.length)} of {shown.length}
      </p>
    </>
  );

  return (
    <div className={classNames('my-projects-mobile', inSelectMode && 'my-projects-mobile--selecting')}>
      <header className='my-projects-mobile__header'>
        <div className='my-projects-mobile__title-row'>
          <h2 className='inspections-due__title'>My Projects</h2>
          <button
            type='button'
            className='my-projects-mobile__icon-btn'
            aria-label='Refresh'
            title='Refresh'
            disabled={isLoading}
            onClick={onRefresh}
          >
            <FontAwesomeIcon icon={faRedoAlt} aria-hidden='true' />
          </button>
          {canSelectHere && (
            <button
              type='button'
              className='my-projects-mobile__text-btn'
              aria-pressed={inSelectMode}
              onClick={() => (inSelectMode ? exitSelectMode() : setSelectMode(true))}
            >
              Select
            </button>
          )}
        </div>

        <div className='my-projects-mobile__views' role='group' aria-label='View'>
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.key}
              type='button'
              aria-pressed={view === option.key}
              onClick={() => changeView(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <SearchInput
          className='my-projects-mobile__search'
          query={search}
          setQuery={onSearchChange}
          aria-label={view === 'ProjectList' ? 'Search projects and clients' : 'Search sites'}
        />
      </header>

      <div {...dragGuardProps}>
        {view !== 'InspectionsDue' && sortButton}
        {view === 'ProjectList' && renderProjectList(inSelectMode, sort)}
        {view === 'Insp. Calendar' &&
          (isLoading ? (
            <Loading what='data' showLoadingText={false} />
          ) : loadFailed ? (
            loadFailedMessage
          ) : (
            <MyProjectsMobileWeek
              rows={allRows}
              sortAgenda={(agenda) => sortRows(agenda, sort, parseDueDate)}
              renderCard={(row, key) => renderCard(row, key, false)}
            />
          ))}
        {view === 'InspectionsDue' && renderInspections()}
      </div>

      <Modal
        show={sortOpen}
        title='Sort by'
        hideButtons
        isSmall
        dismissOnBgClick
        handleClose={closeSort}
        handleSubmit={closeSort}
      >
        <ul className='my-projects-mobile__sort-list'>
          {sortOptions.map((option) => (
            <li key={option.key}>
              <button
                type='button'
                aria-pressed={sort === option.key}
                onClick={() => {
                  setSort(option.key);
                  closeSort();
                }}
              >
                {option.label}
                {sort === option.key && <FontAwesomeIcon icon={faCheck} aria-hidden='true' />}
              </button>
            </li>
          ))}
        </ul>
      </Modal>

      {inSelectMode && (
        <div className='my-projects-mobile__select-bar' role='region' aria-label='Route planner'>
          <span className='my-projects-mobile__select-count' aria-live='polite'>
            {selectedCount} selected{selectedCount >= MAX_STOPS ? ' (max)' : ''}
          </span>
          <button type='button' className='my-projects-mobile__text-btn' onClick={exitSelectMode}>
            Cancel
          </button>
          <button
            type='button'
            className='primary my-projects-mobile__directions'
            disabled={selectedCount === 0}
            onClick={onGetDirections}
          >
            Get directions
          </button>
        </div>
      )}
    </div>
  );
};

export default MyProjectsMobile;
